import React, { useState, useEffect } from 'react';
import { Button, Space, Tag, message, Badge, Modal, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, SendOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { INotification, INotificationStats } from '../../common/notification.interface';
import NotificationApi from '../../api/notification/notification.api';
import CModalNotification from '../../components/modal/CModalNotification';
import CTable from '../../components/table/CTable';
import { motion } from 'framer-motion';
import moment, { Moment } from 'moment';
import './notification.styles.scss';

// Remove unused destructuring

interface NotificationPageState {
  notifications: INotification[];
  loading: boolean;
  isModalOpen: boolean;
  editingNotification: INotification | null;
  currentPage: number;
  pageSize: number;
  total: number;
  searchText: string;
  statusFilter: string;
  typeFilter: string;
  targetTypeFilter: string;
  dateRange: [Moment, Moment] | null;
  stats: INotificationStats | null;
}

const NotificationPage: React.FC = () => {
  const [state, setState] = useState<NotificationPageState>({
    notifications: [],
    loading: false,
    isModalOpen: false,
    editingNotification: null,
    currentPage: 1,
    pageSize: 20,
    total: 0,
    searchText: '',
    statusFilter: '',
    typeFilter: '',
    targetTypeFilter: '',
    dateRange: null,
    stats: null,
  });

  // Load notifications data
  const loadNotifications = async () => {
    console.log('=== START loadNotifications ===');
    setState(prev => ({ ...prev, loading: true }));

    try {
      const params = {
        page: state.currentPage,
        limit: state.pageSize,
        search: state.searchText || undefined,
        status: state.statusFilter || undefined,
        type: state.typeFilter || undefined,
        targetType: state.targetTypeFilter || undefined,
        startDate: state.dateRange?.[0]?.format('YYYY-MM-DD') || undefined,
        endDate: state.dateRange?.[1]?.format('YYYY-MM-DD') || undefined,
      };

      console.log('Loading notifications with params:', params);
      console.log('Current state:', state);

      const subscription = NotificationApi.getAllNotifications(params).subscribe({
        next: (response: any) => {
          console.log('=== API Response received ===');
          console.log('Response:', response);
          console.log('Response.data:', response?.data);
          console.log('Response.pagination:', response?.pagination);

          const notifications = response?.data || [];
          const total = response?.pagination?.totalItems || 0;

          console.log('Setting notifications:', notifications);
          console.log('Setting total:', total);

          setState(prev => ({
            ...prev,
            notifications: notifications,
            total: total,
            loading: false,
          }));
        },
        error: (error) => {
          console.error('=== API Error ===', error);
          message.error('Không thể tải danh sách thông báo');
          setState(prev => ({ ...prev, loading: false }));
        }
      });

      // Store subscription for cleanup if needed
      console.log('Subscription created:', subscription);

    } catch (error) {
      console.error('=== Catch Error ===', error);
      message.error('Có lỗi xảy ra khi tải dữ liệu');
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  // Load notification statistics
  const loadStats = async () => {
    try {
      NotificationApi.getNotificationStats().subscribe({
        next: (stats) => {
          setState(prev => ({ ...prev, stats }));
        },
        error: (error) => {
          console.error('Error loading stats:', error);
        }
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  useEffect(() => {
    loadNotifications();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentPage, state.pageSize, state.searchText, state.statusFilter, state.typeFilter, state.targetTypeFilter, state.dateRange]);

  // Handle table pagination
  const handleTableChange = (pagination: any) => {
    setState(prev => ({
      ...prev,
      currentPage: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  // Handle search
  const handleSearch = (value: string) => {
    setState(prev => ({
      ...prev,
      searchText: value,
      currentPage: 1,
    }));
  };

  // Handle filter changes
  const handleFilterChange = (key: string, value: any) => {
    setState(prev => ({
      ...prev,
      [key]: value,
      currentPage: 1,
    }));
  };

  // Handle create new notification
  const handleCreate = () => {
    setState(prev => ({
      ...prev,
      isModalOpen: true,
      editingNotification: null,
    }));
  };

  // Handle edit notification
  const handleEdit = (notification: INotification) => {
    setState(prev => ({
      ...prev,
      isModalOpen: true,
      editingNotification: notification,
    }));
  };

  // Handle delete notification
  const handleDelete = async (id: string) => {
    try {
      NotificationApi.deleteNotification(id).subscribe({
        next: () => {
          message.success('Xóa thông báo thành công');
          loadNotifications();
          loadStats();
        },
        error: (error) => {
          console.error('Error deleting notification:', error);
          message.error('Không thể xóa thông báo');
        }
      });
    } catch (error) {
      console.error('Error deleting notification:', error);
      message.error('Có lỗi xảy ra khi xóa thông báo');
    }
  };

  // Handle send notification
  const handleSend = async (id: string) => {
    try {
      NotificationApi.sendNotification(id).subscribe({
        next: (response) => {
          if (response.success) {
            message.success(`Gửi thông báo thành công đến ${response.sentCount} thiết bị`);
            loadNotifications();
            loadStats();
          } else {
            message.error(response.message || 'Không thể gửi thông báo');
          }
        },
        error: (error) => {
          console.error('Error sending notification:', error);
          message.error('Có lỗi xảy ra khi gửi thông báo');
        }
      });
    } catch (error) {
      console.error('Error sending notification:', error);
      message.error('Có lỗi xảy ra khi gửi thông báo');
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setState(prev => ({
      ...prev,
      isModalOpen: false,
      editingNotification: null,
    }));
  };

  // Handle modal submit
  const handleModalSubmit = () => {
    loadNotifications();
    loadStats();
    handleModalClose();
  };

  // Note: handleResetFilters removed as it's not used with CTable

  // Get status tag color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'default';
      case 'SCHEDULED': return 'blue';
      case 'SENT': return 'green';
      case 'CANCELLED': return 'red';
      default: return 'default';
    }
  };

  // Get status text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'Nháp';
      case 'SCHEDULED': return 'Đã lên lịch';
      case 'SENT': return 'Đã gửi';
      case 'CANCELLED': return 'Đã hủy';
      default: return status;
    }
  };

  // Get type tag color
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'INFO': return 'blue';
      case 'SUCCESS': return 'green';
      case 'WARNING': return 'orange';
      case 'ERROR': return 'red';
      case 'PROMOTION': return 'purple';
      case 'SYSTEM': return 'cyan';
      case 'BOOK_UPDATE': return 'magenta';
      case 'ORDER_UPDATE': return 'geekblue';
      case 'VIP_UPDATE': return 'gold';
      default: return 'default';
    }
  };

  // Get type text
  const getTypeText = (type: string) => {
    switch (type) {
      case 'INFO': return 'Thông tin';
      case 'SUCCESS': return 'Thành công';
      case 'WARNING': return 'Cảnh báo';
      case 'ERROR': return 'Lỗi';
      case 'PROMOTION': return 'Khuyến mãi';
      case 'SYSTEM': return 'Hệ thống';
      case 'BOOK_UPDATE': return 'Cập nhật sách';
      case 'ORDER_UPDATE': return 'Cập nhật đơn hàng';
      case 'VIP_UPDATE': return 'Cập nhật VIP';
      default: return type;
    }
  };

  // Get target type text
  const getTargetTypeText = (targetType: string) => {
    switch (targetType) {
      case 'ALL': return 'Tất cả';
      case 'VIP': return 'VIP';
      case 'SPECIFIC': return 'Cụ thể';
      default: return targetType;
    }
  };

  // Table columns
  const columns: ColumnsType<INotification> = [
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      width: 250,
      render: (text: string, record: INotification) => (
        <div>
          <div className="notification-title">{text}</div>
          <div className="notification-message">{record.message}</div>
        </div>
      ),
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>{getTypeText(type)}</Tag>
      ),
    },
    {
      title: 'Đối tượng',
      dataIndex: 'targetType',
      key: 'targetType',
      width: 120,
      render: (targetType: string, record: INotification) => (
        <div>
          <Tag color="blue">{getTargetTypeText(targetType)}</Tag>
          {record.vipTierId && <div className="vip-tier">VIP Tier {record.vipTierId}</div>}
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: 'Thống kê',
      key: 'stats',
      width: 150,
      render: (_, record: INotification) => (
        <div className="notification-stats">
          {record.status === 'SENT' && (
            <>
              <div>Gửi: <Badge count={record.sentCount || 0} showZero color="blue" /></div>
              <div>Đọc: <Badge count={record.readCount || 0} showZero color="green" /></div>
              <div>Click: <Badge count={record.clickCount || 0} showZero color="orange" /></div>
            </>
          )}
        </div>
      ),
    },
    {
      title: 'Thời gian',
      key: 'time',
      width: 180,
      render: (_, record: INotification) => (
        <div>
          <div>Tạo: {moment(record.createdAt).format('DD/MM/YYYY HH:mm')}</div>
          {record.scheduledTime && (
            <div>Lên lịch: {moment(record.scheduledTime).format('DD/MM/YYYY HH:mm')}</div>
          )}
          {record.sentAt && (
            <div>Gửi: {moment(record.sentAt).format('DD/MM/YYYY HH:mm')}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record: INotification) => (
        <Space size="small">
          <Tooltip title="Xem chi tiết">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          {record.status !== 'SENT' && (
            <Tooltip title="Chỉnh sửa">
              <Button
                icon={<EditOutlined />}
                size="small"
                type="primary"
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}
          {(record.status === 'DRAFT' || record.status === 'SCHEDULED') && (
            <Tooltip title="Gửi thông báo">
              <Button
                icon={<SendOutlined />}
                size="small"
                type="primary"
                onClick={() => {
                  Modal.confirm({
                    title: 'Xác nhận gửi thông báo',
                    content: 'Bạn có chắc muốn gửi thông báo này? Sau khi gửi sẽ không thể chỉnh sửa hoặc xóa.',
                    okText: 'Gửi ngay',
                    cancelText: 'Hủy',
                    onOk: () => handleSend(record.id),
                  });
                }}
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              />
            </Tooltip>
          )}
          {record.status !== 'SENT' && (
            <Tooltip title="Xóa thông báo">
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                onClick={() => {
                  Modal.confirm({
                    title: 'Xác nhận xóa',
                    content: 'Bạn có chắc muốn xóa thông báo này? Hành động này không thể hoàn tác.',
                    okText: 'Xóa',
                    cancelText: 'Hủy',
                    okType: 'danger',
                    onOk: () => handleDelete(record.id),
                  });
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <motion.div
      className="notification-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <CTable
        tableMainTitle="Quản lý thông báo"
        allowTextSearch={true}
        allowDateRangeSearch={true}
        allowSelectBox={true}
        allowAddObject={true}
        selectBoxData={[
          { value: '', label: 'Tất cả trạng thái' },
          { value: 'DRAFT', label: 'Nháp' },
          { value: 'SCHEDULED', label: 'Đã lên lịch' },
          { value: 'SENT', label: 'Đã gửi' },
          { value: 'CANCELLED', label: 'Đã hủy' }
        ]}
        selectBoxPlaceholder="Chọn trạng thái"
        titleOfColumnList={columns}
        data={state.notifications}
        totalRecord={state.total}
        titleAddButton="Thêm thông báo"
        pageSize={state.pageSize}
        textSearch={state.searchText}
        loading={state.loading}
        onChangeInput={(e) => setState(prev => ({ ...prev, searchText: e.target.value }))}
        onChangeRangePicker={(dates) => handleFilterChange('dateRange', dates)}
        onChangeSelectBox={(value) => handleFilterChange('statusFilter', value)}
        onSearch={() => handleSearch(state.searchText)}
        onChangePagination={handleTableChange}
        handleAddObject={handleCreate}
      />

      {/* Modal for Create/Edit */}
      <CModalNotification
        open={state.isModalOpen}
        onCancel={handleModalClose}
        onSubmit={handleModalSubmit}
        notification={state.editingNotification}
      />
    </motion.div>
  );
};

export default NotificationPage;